import { useState, useEffect } from 'react'
import { Row, Col, Card, Button, Table, Form, Alert, Spinner, Modal, Badge, ProgressBar } from 'react-bootstrap'
import { api } from '../../services/api'

interface SLAPolicy {
  id: string
  name: string
  description?: string
  firstResponseMins: number  // API returns minutes
  resolutionMins: number      // API returns minutes
  priority: string
  category?: string
  isActive: boolean
  createdAt: string
  _count: {
    tickets: number
  }
}

interface SLAData {
  name: string
  description: string
  responseTimeHours: number
  resolutionTimeHours: number
  priority: string
  category: string
}

export const SLAPoliciesPage = () => {
  const [policies, setPolicies] = useState<SLAPolicy[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingPolicy, setEditingPolicy] = useState<SLAPolicy | null>(null)
  const [policyData, setPolicyData] = useState<SLAData>({
    name: '',
    description: '',
    responseTimeHours: 24,
    resolutionTimeHours: 72,
    priority: '',
    category: ''
  })
  const [submitting, setSubmitting] = useState(false)
  const [priorities, setPriorities] = useState<any[]>([])
  const [categories, setCategories] = useState<any[]>([])

  useEffect(() => {
    fetchPolicies()
    fetchPriorities()
    fetchCategories()
  }, [])

  const fetchPolicies = async () => {
    try {
      setLoading(true)
      const response = await api.get('/admin/sla-policies')
      setPolicies(response.data.data || [])
    } catch (err: any) {
      setError('Failed to load SLA policies')
    } finally {
      setLoading(false)
    }
  }

  const fetchPriorities = async () => {
    try {
      const response = await api.get('/admin/priorities')
      setPriorities(response.data.data || [])
    } catch (err) {
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await api.get('/admin/categories')
      setCategories(response.data.data || [])
    } catch (err) {
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      // Convert hours to minutes for API
      const apiData = {
        name: policyData.name,
        priority: policyData.priority,
        firstResponseMins: Math.round(policyData.responseTimeHours * 60),
        resolutionMins: Math.round(policyData.resolutionTimeHours * 60)
      }

      if (editingPolicy) {
        await api.patch(`/admin/sla-policies/${editingPolicy.id}`, apiData)
      } else {
        await api.post('/admin/sla-policies', apiData)
      }
      setShowModal(false)
      setEditingPolicy(null)
      setPolicyData({
        name: '',
        description: '',
        responseTimeHours: 24,
        resolutionTimeHours: 72,
        priority: '',
        category: ''
      })
      fetchPolicies()
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save SLA policy')
    } finally {
      setSubmitting(false)
    }
  }

  const handleEdit = (policy: SLAPolicy) => {
    setEditingPolicy(policy)
    setPolicyData({
      name: policy.name,
      description: policy.description || '',
      // Convert minutes to hours for display
      responseTimeHours: Math.round(policy.firstResponseMins / 60),
      resolutionTimeHours: Math.round(policy.resolutionMins / 60),
      priority: policy.priority,
      category: policy.category || ''
    })
    setShowModal(true)
  }

  const handleDelete = async (policyId: string) => {
    if (window.confirm('Are you sure you want to delete this SLA policy?')) {
      try {
        await api.delete(`/admin/sla-policies/${policyId}`)
        fetchPolicies()
      } catch (err: any) {
        setError(err.response?.data?.error?.message || 'Failed to delete SLA policy')
      }
    }
  }

  const handleToggleStatus = async (policyId: string, isActive: boolean) => {
    try {
      await api.patch(`/admin/sla-policies/${policyId}`, { isActive: !isActive })
      fetchPolicies()
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update SLA policy')
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Urgent': return 'danger'
      case 'High': return 'warning'
      case 'Medium': return 'info'
      case 'Low': return 'secondary'
      default: return 'secondary'
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  return (
    <div className="sla-policies-page">
      {/* Header */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3">
        <div className="mb-2 mb-sm-0">
          <h2 className="mb-1">SLA Policies</h2>
          <p className="text-muted mb-0 small">Define service level agreements for ticket response and resolution</p>
        </div>
        <Button variant="primary" onClick={() => setShowModal(true)} className="d-flex align-items-center">
          <i className="fas fa-plus me-2"></i>
          Add SLA Policy
        </Button>
      </div>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-primary">{policies.length}</h3>
              <p className="text-muted mb-0">Total Policies</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-success">{policies.filter(p => p.isActive).length}</h3>
              <p className="text-muted mb-0">Active Policies</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-info">{policies.reduce((sum, p) => sum + p._count.tickets, 0)}</h3>
              <p className="text-muted mb-0">Covered Tickets</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-warning">
                {policies.length > 0 ? Math.round(policies.reduce((sum, p) => sum + (p.firstResponseMins / 60), 0) / policies.length) : 0}h
              </h3>
              <p className="text-muted mb-0">Avg Response Time</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* SLA Policies Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-bottom">
          <h5 className="mb-0">
            <i className="fas fa-clock me-2 text-primary"></i>
            SLA Policies ({policies.length})
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          {error && (
            <Alert variant="danger" className="m-3">
              {error}
            </Alert>
          )}
          
          {policies.length > 0 ? (
            <Table hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th>Policy</th>
                  <th>Priority</th>
                  <th>Category</th>
                  <th>Response Time</th>
                  <th>Resolution Time</th>
                  <th>Tickets</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {policies.map((policy) => (
                  <tr key={policy.id}>
                    <td>
                      <div>
                        <div className="fw-medium">{policy.name}</div>
                        {policy.description && (
                          <small className="text-muted">{policy.description}</small>
                        )}
                      </div>
                    </td>
                    <td>
                      <Badge bg={getPriorityColor(policy.priority)}>
                        {policy.priority}
                      </Badge>
                    </td>
                    <td>
                      <span className="text-muted">
                        {policy.category || 'All Categories'}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="me-2">
                          <strong>{Math.round(policy.firstResponseMins / 60)}h</strong>
                        </div>
                        <div className="flex-grow-1">
                          <ProgressBar 
                            variant="info" 
                            now={Math.min(((policy.firstResponseMins / 60) / 48) * 100, 100)} 
                            style={{ height: '6px' }}
                          />
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="me-2">
                          <strong>{Math.round(policy.resolutionMins / 60)}h</strong>
                        </div>
                        <div className="flex-grow-1">
                          <ProgressBar 
                            variant="success" 
                            now={Math.min(((policy.resolutionMins / 60) / 168) * 100, 100)} 
                            style={{ height: '6px' }}
                          />
                        </div>
                      </div>
                    </td>
                    <td>
                      <Badge bg="info">{policy._count.tickets}</Badge>
                    </td>
                    <td>
                      <Badge bg={policy.isActive ? 'success' : 'danger'}>
                        {policy.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>
                      <span className="text-muted">
                        {formatDate(policy.createdAt)}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => handleEdit(policy)}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button 
                          variant={policy.isActive ? "outline-warning" : "outline-success"} 
                          size="sm"
                          onClick={() => handleToggleStatus(policy.id, policy.isActive)}
                        >
                          <i className={`fas fa-${policy.isActive ? 'ban' : 'check'}`}></i>
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleDelete(policy.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-5">
              <i className="fas fa-clock text-muted" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3 text-muted">No SLA policies found</h5>
              <p className="text-muted">Create your first SLA policy using the button above</p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* SLA Policy Modal */}
      <Modal show={showModal} onHide={() => {
        setShowModal(false)
        setEditingPolicy(null)
        setPolicyData({
          name: '',
          description: '',
          responseTimeHours: 24,
          resolutionTimeHours: 72,
          priority: '',
          category: ''
        })
      }}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingPolicy ? 'Edit SLA Policy' : 'Add New SLA Policy'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Policy Name</Form.Label>
              <Form.Control
                type="text"
                value={policyData.name}
                onChange={(e) => setPolicyData(prev => ({ ...prev, name: e.target.value }))}
                required
                placeholder="e.g., Standard Support SLA"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={policyData.description}
                onChange={(e) => setPolicyData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what this SLA policy covers..."
              />
            </Form.Group>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select
                    value={policyData.priority}
                    onChange={(e) => setPolicyData(prev => ({ ...prev, priority: e.target.value }))}
                    required
                  >
                    <option value="">Select Priority</option>
                    {priorities.map((priority) => (
                      <option key={priority.id} value={priority.name}>{priority.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Category (Optional)</Form.Label>
                  <Form.Select
                    value={policyData.category}
                    onChange={(e) => setPolicyData(prev => ({ ...prev, category: e.target.value }))}
                  >
                    <option value="">All Categories</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.name}>{category.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Response Time (Hours)</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="168"
                    value={policyData.responseTimeHours}
                    onChange={(e) => setPolicyData(prev => ({ ...prev, responseTimeHours: parseInt(e.target.value) }))}
                    required
                  />
                  <Form.Text className="text-muted">
                    Time to first response (1-168 hours)
                  </Form.Text>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Resolution Time (Hours)</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="720"
                    value={policyData.resolutionTimeHours}
                    onChange={(e) => setPolicyData(prev => ({ ...prev, resolutionTimeHours: parseInt(e.target.value) }))}
                    required
                  />
                  <Form.Text className="text-muted">
                    Time to resolution (1-720 hours)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowModal(false)
            setEditingPolicy(null)
            setPolicyData({
              name: '',
              description: '',
              responseTimeHours: 24,
              resolutionTimeHours: 72,
              priority: '',
              category: ''
            })
          }}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={submitting || !policyData.name.trim() || !policyData.priority}
          >
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                {editingPolicy ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <i className="fas fa-save me-2"></i>
                {editingPolicy ? 'Update Policy' : 'Create Policy'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

